import { useEffect, useRef, useState } from "react";
import http, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Trash2, Eye, EyeOff, ImageIcon } from "lucide-react";

const CATEGORIES = [
  ["intake", "При приёмке"], ["label", "Шильдик / серийный"], ["damage", "Повреждения"],
  ["error_display", "Ошибка на дисплее"], ["accessories", "Аксессуары"], ["diagnosis", "Диагностика"],
  ["repair", "Ремонт"], ["after_repair", "После ремонта"], ["other", "Другое"],
];
const catLabel = Object.fromEntries(CATEGORIES);

function fileUrl(id) {
  const token = localStorage.getItem("admin_token");
  return `${API}/v1/admin/photos/${id}/file?auth=${token}`;
}

export default function AdminRepairPhotos({ orderId }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("intake");
  const [visibility, setVisibility] = useState("internal");
  const [caption, setCaption] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const load = () => http.get(`/v1/admin/repair-orders/${orderId}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => {
    if (orderId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Файл слишком большой (макс. 10 МБ)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("visibility", visibility);
    fd.append("caption", caption);
    setUploading(true);
    try {
      await http.post(`/v1/admin/repair-orders/${orderId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Фото загружено"); setCaption(""); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ошибка загрузки");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setCat = async (id, cat) => { try { await http.patch(`/v1/admin/photos/${id}`, { category: cat }); load(); } catch { toast.error("Ошибка"); } };
  const toggleVis = async (p) => {
    const next = p.visibility === "customer" ? "internal" : "customer";
    try { await http.patch(`/v1/admin/photos/${p.id}`, { visibility: next }); toast.success(next === "customer" ? "Видно клиенту" : "Внутреннее"); load(); } catch { toast.error("Ошибка"); }
  };
  const remove = async (id) => { if (!window.confirm("Удалить фото?")) return; try { await http.delete(`/v1/admin/photos/${id}`); toast.success("Удалено"); load(); } catch { toast.error("Ошибка"); } };

  return (
    <div className="space-y-3" data-testid="repair-photos">
      <div className="flex flex-wrap items-end gap-2 bg-slate-900/40 border border-slate-700 rounded-lg p-3">
        <div>
          <label className="text-xs text-slate-400">Категория</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 block bg-slate-700 border border-slate-600 text-white rounded px-2 py-2 text-sm" data-testid="photo-category">
            {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400">Видимость</label>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="mt-1 block bg-slate-700 border border-slate-600 text-white rounded px-2 py-2 text-sm" data-testid="photo-visibility">
            <option value="internal">Внутреннее</option>
            <option value="customer">Видно клиенту</option>
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="text-xs text-slate-400">Подпись (необязательно)</label>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="photo-caption" />
        </div>
        <input ref={fileRef} type="file" accept="image/*,.heic,.heif" onChange={onFile} className="hidden" data-testid="photo-file-input" />
        <Button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="photo-upload-btn">
          <Upload className="h-4 w-4" />{uploading ? "Загрузка..." : "Загрузить фото"}
        </Button>
      </div>

      {photos.length === 0 ? (
        <p className="text-sm text-slate-500 flex items-center gap-2"><ImageIcon className="h-4 w-4" />Фото пока нет. По умолчанию новые фото — внутренние.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="border border-slate-700 rounded-lg overflow-hidden bg-slate-900/40" data-testid={`photo-item-${p.id}`}>
              <button type="button" onClick={() => setLightbox(p)} className="block w-full">
                <img src={fileUrl(p.id)} alt={p.caption || p.category} className="w-full h-28 object-cover" loading="lazy" />
              </button>
              <div className="p-2 space-y-1">
                <select value={p.category} onChange={(e) => setCat(p.id, e.target.value)} className="w-full bg-slate-700 border border-slate-600 text-white rounded px-1 py-1 text-xs">
                  {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                {p.caption && <p className="text-xs text-slate-400 truncate" title={p.caption}>{p.caption}</p>}
                <div className="flex items-center justify-between">
                  <button type="button" onClick={() => toggleVis(p)} title="Переключить видимость"
                    className={`text-xs px-1.5 py-0.5 rounded border flex items-center gap-1 ${p.visibility === "customer" ? "bg-green-900/40 text-green-300 border-green-700/50" : "bg-slate-700 text-slate-300 border-slate-600"}`}
                    data-testid={`photo-vis-${p.id}`}>
                    {p.visibility === "customer" ? <><Eye className="h-3 w-3" />Клиенту</> : <><EyeOff className="h-3 w-3" />Внутр.</>}
                  </button>
                  <button type="button" onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300" data-testid={`photo-del-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-4xl p-2">
          {lightbox && <img src={fileUrl(lightbox.id)} alt={lightbox.caption || lightbox.category} className="w-full max-h-[80vh] object-contain" />}
          {lightbox?.caption && <p className="text-sm text-slate-300 px-2 py-1">{catLabel[lightbox.category]} — {lightbox.caption}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
